import {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  EndSessionRequest,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
  SsoSilentRequest,
} from "@azure/msal-browser";
import { AuthError, InteractionRequiredAuthError } from "@azure/msal-browser";
import { AuthenticationState } from "react-aad-msal";

import { tdsWebApiScopes } from "./authProvider";

// Type definitions for Handlers that will listen to
// internal state changes within AuthenticationProvider
type AuthenticationStateHandler = (state: AuthenticationState) => void;
type ErrorHandler = (error: AuthError | undefined) => void;

const LAST_SIGNED_IN_KEY = "LAST_SIGNED_IN_TDSWEB_KEY";

// MSAL renews tokens silently in a hidden iframe. By default that iframe lands on
// the app's redirectUri after AAD, which reloads the entire SPA inside the iframe
// and prevents MSAL from reading the response before its iframe timeout (~10s).
// Pointing the silent flow at a lightweight blank page lets MSAL harvest the
// response immediately. This URL must be registered as a SPA reply URL on the app
// registration. Only the silent (iframe) flow uses it; the interactive redirect
// login still uses the app's own redirectUri.
const SILENT_REDIRECT_URI = `${window.location.origin}${process.env.PUBLIC_URL}/blank.html`;

export default class AuthenticationProvider extends PublicClientApplication {
  public authenticationState: AuthenticationState;

  // Private properties
  private currentAccount: AccountInfo | undefined;
  private apiRequest: RedirectRequest;
  private error: AuthError | undefined;

  // Listeners
  private onAuthenticationStateHandlers = new Set<AuthenticationStateHandler>();
  private onErrorHandlers = new Set<ErrorHandler>();

  /**
   * Initialize and set up the AuthenticationProvider
   *
   * @param msalConfig MSAL Configuration for the provider to utilize
   */
  constructor(msalConfig: Configuration) {
    // Initialize the MSAL instance (PublicClientApplication)
    super(msalConfig);

    this.authenticationState = AuthenticationState.Unauthenticated;

    // Default scopes that we will request when signing in (for the purposes
    // of the TDS Web, this creates a token who's audience is our backend API)
    this.apiRequest = {
      scopes: tdsWebApiScopes,
    };
  }

  /**
   * For the current user, get a TDS Web API token
   */
  async getApiToken(): Promise<AuthenticationResult> {
    if (this.currentAccount === undefined) {
      throw Error("Not logged in, make sure you are logged in before executing this method.");
    }

    const silentLoginRequest: SilentRequest = {
      ...this.apiRequest,
      account: this.currentAccount,
      redirectUri: SILENT_REDIRECT_URI,
    };

    return await this.acquireTokenSilent(silentLoginRequest);
  }

  /**
   * Gets the current account
   */
  getAccount(): AccountInfo {
    if (this.currentAccount === undefined) {
      throw Error("Not logged in, make sure you are logged in before executing this method.");
    }

    return this.currentAccount;
  }

  /**
   * Used by AuthenticationProvider.login() to determine which accounts that
   * MSAL has cached in LocalStorage, and then which of those was the account
   * that was last signed in, and signs in that account first.
   */
  getLastSignedInAccount(): AccountInfo | undefined {
    const allAccounts = this.getAllAccounts();

    // If there are no accounts in cache, return
    if (allAccounts.length === 0) return undefined;

    const lastSignedInUsername = localStorage.getItem(LAST_SIGNED_IN_KEY);

    if (lastSignedInUsername) {
      return allAccounts.find((account) => account.username === lastSignedInUsername);
    } else {
      return allAccounts[0];
    }
  }

  async login(): Promise<void> {
    await this.initialize();
    this.setAuthenticationState(AuthenticationState.InProgress);

    // First check to see if we are in the process of handling a redirect
    const tokenResponse = await this.handleRedirectPromise();
    if (tokenResponse !== null) {
      this.processLoginTokenResponse(tokenResponse);
      return;
    }

    // If we are here, attempt to get current account from cache and perform a silent SSO for that account.
    const lastSignedInAccount = this.getLastSignedInAccount();
    if (lastSignedInAccount) {
      return await this.trySilentLogin(lastSignedInAccount);
    }

    // If we are here, there were no accounts from cache, so perform the default redirect login
    await this.loginRedirect(this.apiRequest);
  }

  /**
   * Given an account object, sign in and set that as the primary account
   *
   * @param account The account to sign in to
   */
  async loginWithAccount(account: AccountInfo): Promise<void> {
    this.currentAccount = undefined;
    this.setAuthenticationState(AuthenticationState.InProgress);

    const silentRequest: SsoSilentRequest = {
      ...this.apiRequest,
      account: account,
      redirectUri: SILENT_REDIRECT_URI,
    };

    const tokenResponse = await this.ssoSilent(silentRequest);
    this.processLoginTokenResponse(tokenResponse);
  }

  /**
   * Logout of the current account that is signed in, after logout, will be redirected
   * to the application's home.
   */
  logoutCurrentAccount(): void {
    const logoutRequest: EndSessionRequest = {
      account: this.currentAccount,
    };

    this.logoutRedirect(logoutRequest);
  }

  /**
   * Upon conclusion of a login request, set the currentAccount, update the LAST_SIGNED_IN_KEY
   * in LocalStorage, and then set the authentication state.
   *
   * @param tokenResponse The token response from a silent/redirect login request
   */
  private processLoginTokenResponse = (tokenResponse: AuthenticationResult) => {
    if (tokenResponse.account === null) {
      throw new Error("Account should not be null, this method processLoginTokenResponse should not have been called.");
    }

    this.currentAccount = tokenResponse.account;

    localStorage.setItem(LAST_SIGNED_IN_KEY, this.currentAccount.username);

    this.setAuthenticationState(AuthenticationState.Authenticated);
  };

  /**
   * Given an account, try to acquire it's token silently. If the Account's session
   * is expired, automatically initiate a redirect authentication process.
   *
   * @param account The account
   */
  private trySilentLogin = async (account: AccountInfo) => {
    try {
      const silentRequest: SilentRequest = {
        ...this.apiRequest,
        account: account,
        redirectUri: SILENT_REDIRECT_URI,
      };

      const tokenResponse = await this.acquireTokenSilent(silentRequest);

      this.processLoginTokenResponse(tokenResponse);
    } catch (error) {
      if (
        error instanceof InteractionRequiredAuthError &&
        typeof error === "object" &&
        error !== null &&
        "message" in error &&
        typeof (error as any).message === "string" &&
        (error as any).message.includes("AADSTS160021")
      ) {
        // Remove all MSAL v1 cache keys
        Object.keys(localStorage)
          .filter(key => key.startsWith("msal."))
          .forEach(key => localStorage.removeItem(key));
        // Remove from local storage
        localStorage.removeItem(LAST_SIGNED_IN_KEY);
        this.setAuthenticationState(AuthenticationState.Unauthenticated);
        // Force a fresh login
        await this.loginRedirect(this.apiRequest);
      } else if (error instanceof InteractionRequiredAuthError) {
        this.loginRedirect(this.apiRequest);
      } else if (error instanceof AuthError) {
        // Silent token acquisition failed (e.g. a stale or incompatible cached
        // account left by a previous MSAL/ADAL version). Fall back to interactive
        // login instead of dead-ending at Unauthenticated.
        this.getLogger().error(error.message, error.correlationId);
        await this.loginRedirect(this.apiRequest);
      } else {
        // If we receive an error that's not an AuthError, then something is wrong, throw it
        throw error;
      }
    }
  };

  private setAuthenticationState = (state: AuthenticationState): AuthenticationState => {
    if (this.authenticationState !== state) {
      this.authenticationState = state;

      this.onAuthenticationStateHandlers.forEach((listener) => listener(state));
    }

    return this.authenticationState;
  };

  private setError = (error: AuthError | undefined) => {
    this.error = error;
    this.onErrorHandlers.forEach((listener) => listener(error));
    return { ...this.error };
  };

  public registerAuthenticationStateHandler = (listener: AuthenticationStateHandler): void => {
    this.onAuthenticationStateHandlers.add(listener);
    listener(this.authenticationState);
  };

  public unregisterAuthenticationStateHandler = (listener: AuthenticationStateHandler): void => {
    this.onAuthenticationStateHandlers.delete(listener);
  };

  public registerErrorHandler = (listener: ErrorHandler): void => {
    this.onErrorHandlers.add(listener);
    listener(this.error);
  };

  public unregisterErrorHandler = (listener: ErrorHandler): void => {
    this.onErrorHandlers.delete(listener);
  };
}
